<script setup>
  import { ref, computed, onMounted, nextTick, provide, watch, watchEffect, useSlots } from "vue";

  const emit = defineEmits(['segmentChanged']);

  const props = defineProps({
    label: {
      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    defaultValue: {
      type: [String, Number, Boolean],
      required: false
    },
    direction: {
      type: String,
      default: 'row'
    },
    shape: {
      type: String,
      default: 'rounded'
    },
    iconOnly: {
      type: Boolean,
      default: false
    },
  });

  const model = defineModel();
  const previous = defineModel('previous');

  let activeItemWidth = ref(0);
  let activePosition = ref(0);
  let isUserInput = ref(false);
  let buttonRefs = ref([]);
  let activeValue = ref(model);
  let updateIndicator = ref(false);
  let disabled = ref(props.disabled);
  const slots = useSlots();
  if (!activeValue.value && props.defaultValue) activeValue.value = props.defaultValue;

  // Provided to segment buttons
  provide('buttonRefs', buttonRefs);
  provide('activeValue', activeValue);
  provide('isUserInput', isUserInput);
  provide('updateIndicator', updateIndicator);
  provide('disabled', disabled);
  provide('changeHandler', changeHandler);

  const indicatorStyles = computed(() => ({
      '--activeIndicatorWidth': `${activeItemWidth.value}px`,
      '--activeIndicatorPosition': `${activePosition.value}px`,
    })
  );

  const controlClasses = computed(() => ({
      'animate-indicator': isUserInput.value,
      'jwx-segmented-control': true,
      'jwx-segmented-control--disabled': props.disabled,
    })
  );

  function changeHandler(e) {
    emit('segmentChanged', {
      value: e.target.value,
      previous: previous.value,
    });
    
  }

  async function updateControlIndicator() {
    await nextTick();

    buttonRefs.value.map(ref => {
      if (ref.dataset.active === 'false') return;

      activeItemWidth.value = ref.parentElement.offsetWidth;
      activePosition.value = ref.parentElement.offsetLeft;
    })

    updateIndicator.value = false;
  }

  onMounted(() => updateControlIndicator());

  watch(activeValue, (newValue, oldValue) => {
    previous.value = oldValue;
    model.value = newValue;
    updateControlIndicator();
  })

  watch(updateIndicator, updateControlIndicator);
  watchEffect(() => disabled.value = props.disabled);
</script>

<template>
  <ul 
    role=menu
    :class="controlClasses" 
    :style="indicatorStyles"
    :aria-label="label"
    :data-shape="shape"
    :data-icon-only="iconOnly"
    ref="list"
  >
    <slot></slot>
  </ul>
</template>